import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/dateFormat';

interface FileShareActivity {
  share_id: number;
  email: string;
  permission: string;
  status: string;
  is_active: boolean;
  created_at: string | null;
  verified_at: string | null;
  last_access: string | null;
  expires_at: string | null;
  session_count: number;
}

interface ShareActivityPanelProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: number;
  fileName: string;
}

const STATUS_ICONS: Record<string, { icon: string; color: string; label: string }> = {
  verified: { icon: '\u2705', color: 'text-green-600', label: 'Verified' },
  pending: { icon: '\u23F3', color: 'text-amber-600', label: 'Pending' },
  revoked: { icon: '\uD83D\uDEAB', color: 'text-red-600', label: 'Revoked' },
  expired: { icon: '\u23F0', color: 'text-gray-500', label: 'Expired' },
};

const ShareActivityPanel: React.FC<ShareActivityPanelProps> = ({ isOpen, onClose, fileId, fileName }) => {
  const { token, branding } = useAuth();
  const [shares, setShares] = useState<FileShareActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokeLoading, setRevokeLoading] = useState<number | null>(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const timeAgo = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  };

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/files/${fileId}/share-activity`, { headers });
      if (res.ok) {
        const data = await res.json();
        setShares(data.shares || []);
      }
    } catch (err) {
      console.error('Failed to fetch share activity:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) fetchActivity();
  }, [isOpen, fileId]);

  const handleRevoke = async (shareId: number) => {
    setRevokeLoading(shareId);
    try {
      const res = await fetch(`/api/v1/files/${fileId}/share/${shareId}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        await fetchActivity();
      }
    } catch (err) {
      console.error('Failed to revoke:', err);
    }
    setRevokeLoading(null);
  };

  if (!isOpen) return null;

  const expiryInfo = (share: FileShareActivity) => {
    if (!share.expires_at) return 'Never';
    const exp = new Date(share.expires_at);
    const now = new Date();
    if (exp < now) return 'Expired';
    const days = Math.ceil((exp.getTime() - now.getTime()) / 86400000);
    return `${days} day${days !== 1 ? 's' : ''} remaining`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-lg p-6 shadow-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Share Activity</h3>
            <p className="text-sm text-slate-400 truncate">{fileName}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : shares.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <p>This file hasn't been shared yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {shares.map((share) => {
              const si = STATUS_ICONS[share.status] || STATUS_ICONS.pending;
              return (
                <div key={share.share_id} className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm text-white font-medium">{share.email}</p>
                      <p className={`text-xs ${si.color} mt-0.5`}>
                        {si.icon} {si.label}
                        {share.verified_at && share.status === 'verified' && (
                          <span className="text-slate-500 ml-1">
                            (OTP confirmed {formatDateTime(share.verified_at)})
                          </span>
                        )}
                        {share.status === 'pending' && (
                          <span className="text-slate-500 ml-1">(link sent, not yet opened)</span>
                        )}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500 bg-white/5 px-2 py-0.5 rounded capitalize">
                      {share.permission}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-400 mt-2">
                    <div>Sessions: <span className="text-slate-300">{share.session_count}</span>
                      {share.last_access && (
                        <span className="text-slate-500"> (last: {timeAgo(share.last_access)})</span>
                      )}
                    </div>
                    <div>Expires: <span className="text-slate-300">{expiryInfo(share)}</span></div>
                    {share.created_at && (
                      <div className="col-span-2">Shared: <span className="text-slate-300">{formatDateTime(share.created_at)}</span></div>
                    )}
                  </div>

                  {/* Actions */}
                  {share.is_active && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                      <button
                        onClick={() => handleRevoke(share.share_id)}
                        disabled={revokeLoading === share.share_id}
                        className="px-3 py-1 text-xs font-medium text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/10 disabled:opacity-50 transition-colors"
                      >
                        {revokeLoading === share.share_id ? 'Revoking...' : 'Revoke Access'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end mt-4 pt-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareActivityPanel;
